import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context";

type OpsRole = "guest" | "sena" | "admin";

const ROLE_LABELS: Record<OpsRole, string> = {
  guest: "Guest",
  sena: "Sena (Volunteer)",
  admin: "Admin",
};

const ROLE_DESCS: Record<OpsRole, string> = {
  guest: "View event info, donate, or check in as an attendee.",
  sena: "Assist with check-in, manage walk-ins, and monitor stats.",
  admin: "Full access to check-in, POS, reports, and donor info.",
};

const ROLE_ICONS: Record<OpsRole, string> = {
  guest: "🙏",
  sena: "🌿",
  admin: "⚙️",
};

export function OpsShellPage() {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState<OpsRole | null>(null);

  const allowedRoles: OpsRole[] =
    user?.role === "admin"
      ? ["guest", "sena", "admin"]
      : user
      ? ["guest", "sena"]
      : ["guest"];

  if (selectedRole) {
    return (
      <div className="ops-shell">
        <div className="container">
          {/* Role header */}
          <div className="ops-role-header">
            <span className="ops-role-icon">{ROLE_ICONS[selectedRole]}</span>
            <div>
              <div className="ops-role-label">{ROLE_LABELS[selectedRole]} Mode</div>
              <div className="ops-role-desc">{ROLE_DESCS[selectedRole]}</div>
            </div>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setSelectedRole(null)}
            >
              Switch role
            </button>
          </div>

          {/* Module grid */}
          <div className="ops-module-grid">
            {/* Check-In — all roles */}
            <Link to="/ops/checkin" className="ops-module-card">
              <span className="ops-module-card__icon">📋</span>
              <div className="ops-module-card__title">Check-In</div>
              <div className="ops-module-card__desc">
                Scan QR codes, search attendees, or register walk-ins.
              </div>
            </Link>

            {/* POS — sena + admin */}
            {(selectedRole === "sena" || selectedRole === "admin") && (
              <Link to="/ops/pos" className="ops-module-card">
                <span className="ops-module-card__icon">🛒</span>
                <div className="ops-module-card__title">AekamMart POS</div>
                <div className="ops-module-card__desc">
                  Process retail sales — crystals, bracelets, and sacred items.
                </div>
              </Link>
            )}

            {/* Donation Info — all roles */}
            <Link to="/ops/info" className="ops-module-card">
              <span className="ops-module-card__icon">💛</span>
              <div className="ops-module-card__title">Donation Info</div>
              <div className="ops-module-card__desc">
                Share US &amp; India donation details with attendees.
              </div>
            </Link>

            {/* Admin extras */}
            {selectedRole === "admin" && (
              <>
                <Link to="/admin" className="ops-module-card ops-module-card--admin">
                  <span className="ops-module-card__icon">⚙️</span>
                  <div className="ops-module-card__title">Site Admin</div>
                  <div className="ops-module-card__desc">
                    Full governance console — users, content, and settings.
                  </div>
                </Link>
                <Link to="/events" className="ops-module-card ops-module-card--admin">
                  <span className="ops-module-card__icon">🗓️</span>
                  <div className="ops-module-card__title">Event Management</div>
                  <div className="ops-module-card__desc">
                    View and manage event registrations and ticket tiers.
                  </div>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-entry">
      <div className="container">
        <section className="ops-entry-hero">
          <span className="eyebrow">AekamPass Operations</span>
          <h1 className="display-2">Welcome to the Ops Center</h1>
          <p className="body-lg text-muted">
            Select your role to access the appropriate tools for today's event.
          </p>
        </section>

        <div className="ops-role-grid">
          {allowedRoles.map((role) => (
            <button
              key={role}
              type="button"
              className="ops-role-card"
              onClick={() => setSelectedRole(role)}
            >
              <span className="ops-role-card__icon">{ROLE_ICONS[role]}</span>
              <div className="ops-role-card__title">{ROLE_LABELS[role]}</div>
              <div className="ops-role-card__desc">{ROLE_DESCS[role]}</div>
              <span className="btn btn--outline btn--sm" style={{ marginTop: "1rem" }}>
                Enter as {ROLE_LABELS[role]}
              </span>
            </button>
          ))}
        </div>

        <div className="ops-entry-footer">
          <p className="text-muted" style={{ fontSize: "0.85rem" }}>
            🔒 AekamPass Ops — for authorized event staff only
          </p>
        </div>
      </div>
    </div>
  );
}
